/**
 * Admin Content Page
 *
 * Paginated list of all platform content (sources and drafts), with a type
 * filter, a title search, a created-date range, and a detail view that shows
 * metadata first and opens a body only where the access rules allow.
 * Filters are reflected in the URL query string so a filtered view can be
 * copied and shared.
 */

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  CaretRight as ChevronRight,
  Clock,
  FileText,
  Stack as Layers,
  ArrowsClockwise as RefreshCw,
  User,
  Warning,
} from "@phosphor-icons/react";
import {
  Alert,
  DataTable,
  DataTableBody,
  DataTableHead,
  DataTableRow,
  EmptyState,
  HeaderAction,
  PageHeader,
  type PillTone,
  StatusPill,
} from "@charisol-spark/ui";
import { Button } from "@/components/ui/Button";
import {
  AdminListToolbar,
  type ToolbarSortOption,
} from "@/components/lists/AdminListToolbar";
import {
  type ListUrlConfig,
  sortRows,
  useListUrlState,
} from "@/components/lists/useListUrlState";
import { useLoadMore } from "@/components/lists/useLoadMore";
import { Spinner } from "@/components/ui/Spinner";
import { ContentDetailDialog } from "@/components/content/ContentDetailDialog";
import { useAdminContent } from "@/hooks/useAdmin";
import type { AdminContentItem } from "@/services/admin";
import { getErrorMessage } from "@/services/api";
import { cn } from "@/utils/cn";

const TYPES = [
  { value: undefined, label: "All" },
  { value: "source", label: "Sources" },
  { value: "draft", label: "Drafts" },
] as const;

const STATUS_TONES: Record<string, PillTone> = {
  published: "green",
  draft: "amber",
  processing: "blue",
  error: "red",
};

const LIST_COLUMNS = "minmax(0,1fr) 90px 110px 24px";

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "published", label: "Published" },
  { value: "draft", label: "Draft" },
  { value: "processing", label: "Processing" },
  { value: "error", label: "Error" },
] as const;

const SORT_OPTIONS: readonly ToolbarSortOption[] = [
  { field: "createdAt", label: "Created" },
  { field: "title", label: "Title", kind: "text" },
  { field: "status", label: "Status", kind: "text" },
];

/** Most recent first unless the address says otherwise. */
const URL_CONFIG: ListUrlConfig = {
  filterKeys: ["type", "userId", "status", "createdFrom", "createdTo"],
  sortFields: SORT_OPTIONS.map((o) => o.field),
  defaultSort: { field: "createdAt", dir: "desc" },
};

const SORT_GETTERS: Record<
  string,
  (item: AdminContentItem) => string | undefined
> = {
  createdAt: (i) => i.createdAt,
  title: (i) => (i.title || "").toLowerCase(),
  status: (i) => i.status ?? "",
};

function formatDate(iso?: string) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function AdminContentPage() {
  const list = useListUrlState(URL_CONFIG);
  const { filters } = list;
  const [selectedItem, setSelectedItem] = useState<AdminContentItem | null>(
    null
  );
  const type =
    filters.type === "source" || filters.type === "draft"
      ? filters.type
      : undefined;

  const pager = useLoadMore<AdminContentItem>(
    JSON.stringify([list.search, filters])
  );

  const { data, isLoading, error, refetch, isFetching } = useAdminContent({
    type,
    q: list.search || undefined,
    userId: filters.userId || undefined,
    status: filters.status || undefined,
    createdFrom: filters.createdFrom || undefined,
    createdTo: filters.createdTo || undefined,
    cursor: pager.cursor,
    limit: 25,
  });

  const displayItems = useMemo(
    () =>
      sortRows(
        [...pager.previous, ...(data?.items ?? [])],
        list.sort,
        SORT_GETTERS
      ),
    [pager.previous, data?.items, list.sort]
  );

  return (
    <div className="space-y-6">
      <PageHeader
        variant="classic"
        title="Content"
        description="Browse all platform content items."
        action={
          <HeaderAction
            icon={
              <RefreshCw
                className={cn("h-4 w-4", isFetching && "animate-spin")}
              />
            }
            onClick={() => refetch()}
            disabled={isFetching}
          >
            Refresh
          </HeaderAction>
        }
        tabs={{
          label: "Content type",
          items: TYPES.map(({ value, label }) => ({
            value: value ?? "all",
            label,
          })),
          value: type ?? "all",
          onChange: (v) => list.setFilter("type", v === "all" ? "" : v),
        }}
      />

      <AdminListToolbar
        label="Content"
        search={{
          value: list.search,
          onChange: list.setSearch,
          placeholder: "Search titles and links...",
        }}
        filters={[
          {
            id: "status",
            label: "Status",
            kind: "select",
            options: STATUS_OPTIONS,
            value: filters.status!,
            onChange: (v) => list.setFilter("status", v),
          },
          {
            id: "userId",
            label: "Owner (user ID)",
            kind: "text",
            placeholder: "Any owner",
            value: filters.userId!,
            onChange: (v) => list.setFilter("userId", v.trim()),
          },
          {
            id: "createdFrom",
            label: "Created from",
            kind: "date",
            value: filters.createdFrom!,
            max: filters.createdTo || undefined,
            onChange: (v) => list.setFilter("createdFrom", v),
          },
          {
            id: "createdTo",
            label: "Created to",
            kind: "date",
            value: filters.createdTo!,
            min: filters.createdFrom || undefined,
            onChange: (v) => list.setFilter("createdTo", v),
          },
        ]}
        sort={{
          options: SORT_OPTIONS,
          value: list.sort,
          onChange: list.setSort,
          scope: "page",
        }}
        hasActive={list.hasActive}
        onClear={list.clear}
      />

      {/* Content list */}
      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <Spinner size="md" />
        </div>
      ) : error ? (
        <Alert variant="error">
          {getErrorMessage(error, "Failed to load content.")}
        </Alert>
      ) : (
        <>
          {data?.truncated && (
            <p
              role="status"
              className="flex items-center gap-2 rounded-xl border border-amber-300/60 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-600/30 dark:bg-amber-900/10 dark:text-amber-200"
            >
              <Warning className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              Showing partial results — the scan stopped early. Load more to
              keep going.
            </p>
          )}

          <DataTable>
            {displayItems.length === 0 ? (
              <EmptyState
                icon={FileText}
                title={
                  list.hasActive
                    ? "No content matches your filters."
                    : "No content items found."
                }
              />
            ) : (
              <>
                <DataTableHead columns={LIST_COLUMNS}>
                  <span>Content</span>
                  <span>Type</span>
                  <span>Status</span>
                  <span />
                </DataTableHead>
                <DataTableBody>
                  {displayItems.map((item) => (
                    <div
                      key={item.id}
                      role="link"
                      tabIndex={0}
                      className="focus-visible:bg-accent/30 focus-visible:outline-none"
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          setSelectedItem(item);
                        }
                      }}
                    >
                      <DataTableRow
                        columns={LIST_COLUMNS}
                        onClick={() => setSelectedItem(item)}
                      >
                        <div className="flex min-w-0 items-start gap-3">
                          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-muted">
                            {item.type === "source" ? (
                              <FileText className="h-4 w-4 text-muted-foreground" />
                            ) : (
                              <Layers className="h-4 w-4 text-muted-foreground" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="truncate font-medium">
                              {item.title || "Untitled"}
                            </p>
                            <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                              <Link
                                to={`/users/${item.userId}`}
                                onClick={(e) => e.stopPropagation()}
                                onKeyDown={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 hover:text-primary hover:underline"
                                title={`Open owner ${item.userId}`}
                              >
                                <User className="h-3 w-3" />
                                {item.userName ||
                                  item.userEmail ||
                                  `${item.userId.slice(0, 8)}...`}
                              </Link>
                              <span className="inline-flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatDate(item.createdAt)}
                              </span>
                              {item.platform && (
                                <span className="capitalize">
                                  {item.platform}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div>
                          <StatusPill className="capitalize">
                            {item.type}
                          </StatusPill>
                        </div>
                        <div>
                          {item.status && (
                            <StatusPill
                              tone={STATUS_TONES[item.status] ?? "neutral"}
                              className="capitalize"
                            >
                              {item.status}
                            </StatusPill>
                          )}
                        </div>
                        <ChevronRight className="hidden h-4 w-4 text-muted-foreground md:block" />
                      </DataTableRow>
                    </div>
                  ))}
                </DataTableBody>
              </>
            )}
          </DataTable>

          {data?.cursor && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="sm"
                pill
                onClick={() => pager.loadMore(data?.items ?? [], data?.cursor)}
              >
                Load More
              </Button>
            </div>
          )}
        </>
      )}

      <ContentDetailDialog
        item={selectedItem}
        onOpenChange={(open) => {
          if (!open) setSelectedItem(null);
        }}
      />
    </div>
  );
}
